export type Pt = [number, number]

export type StrokeWeight = 'cut' | 'line' | 'thin' | 'hidden' | 'accent'

export interface Stroke {
    points: Array<Pt>
    closed?: boolean
    weight: StrokeWeight
}

export interface Dimension {
    a: Pt
    b: Pt
    offset: number
    label?: string
}

export interface GridLine {
    from: Pt
    to: Pt
    label: string
}

export interface LevelMark {
    at: Pt
    label: string
}

export interface Note {
    at: Pt
    text: string
    anchor?: 'start' | 'middle' | 'end'
    tone?: 'ink' | 'mute' | 'signal'
}

export interface Drawing {
    scale: string
    strokes: Array<Stroke>
    dims: Array<Dimension>
    grids: Array<GridLine>
    levels: Array<LevelMark>
    notes: Array<Note>
    north?: Pt
    deviation?: Pt
}

export type DrawingKind = 'plan' | 'section' | 'elevation' | 'axonometric'

export type DrawingSet = Record<DrawingKind, Drawing>

/**
 * Small builder so each building's drawings read as a list of instructions.
 */
export class Sheet {
    readonly drawing: Drawing

    constructor(scale: string) {
        this.drawing = { scale, strokes: [], dims: [], grids: [], levels: [], notes: [] }
    }

    line(a: Pt, b: Pt, weight: StrokeWeight = 'line'): this {
        this.drawing.strokes.push({ points: [a, b], weight })

        return this
    }

    poly(points: Array<Pt>, weight: StrokeWeight = 'line', closed: boolean = false): this {
        this.drawing.strokes.push({ points, weight, closed })

        return this
    }

    rect(x0: number, y0: number, x1: number, y1: number, weight: StrokeWeight = 'line'): this {
        return this.poly([[x0, y0], [x1, y0], [x1, y1], [x0, y1]], weight, true)
    }

    dim(a: Pt, b: Pt, offset: number, label?: string): this {
        this.drawing.dims.push({ a, b, offset, label })

        return this
    }

    grid(from: Pt, to: Pt, label: string): this {
        this.drawing.grids.push({ from, to, label })

        return this
    }

    level(at: Pt, label: string): this {
        this.drawing.levels.push({ at, label })

        return this
    }

    note(at: Pt, text: string, anchor: Note['anchor'] = 'start', tone: Note['tone'] = 'mute'): this {
        this.drawing.notes.push({ at, text, anchor, tone })

        return this
    }

    north(at: Pt): this {
        this.drawing.north = at

        return this
    }

    deviation(at: Pt): this {
        this.drawing.deviation = at

        return this
    }
}

/**
 * Isometric projection used by every axonometric: x to the right and back, z to the left and back.
 */
export const iso = (x: number, y: number, z: number): Pt => [(x - z) * 0.866, y - (x + z) * 0.5]

/**
 * Visible edges of an axis-aligned box seen from the iso view.
 */
export const isoBox = (sheet: Sheet, x0: number, z0: number, x1: number, z1: number, y0: number, y1: number, weight: StrokeWeight = 'line'): void => {
    sheet.poly([iso(x0, y1, z0), iso(x1, y1, z0), iso(x1, y1, z1), iso(x0, y1, z1)], weight, true)
    sheet.line(iso(x1, y1, z1), iso(x1, y0, z1), weight)
    sheet.line(iso(x0, y1, z1), iso(x0, y0, z1), weight)
    sheet.line(iso(x1, y1, z0), iso(x1, y0, z0), weight)
    sheet.line(iso(x0, y0, z1), iso(x1, y0, z1), weight)
    sheet.line(iso(x1, y0, z0), iso(x1, y0, z1), weight)
}

export const mm = (metres: number): string => String(Math.round(metres * 1000)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

export const levelLabel = (metres: number): string => `${metres >= 0 ? '+' : '−'}${Math.abs(metres).toFixed(2)}`
